// src/App.jsx
import React, { lazy, Suspense, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import routes, { isAscLoginPath } from './config/routes';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Core components (keep these eagerly loaded as they're used frequently)
import VirtualPATLayout from './components/VirtualPATLayout';
import WorkbenchLayout from './workbench/WorkbenchLayout';
import ProtectedRoute from './components/ProtectedRoute';
import ASCProtectedRoute from './components/ASCProtectedRoute';
import RoleProtectedRoute, { ROLE_GROUPS, getUserDashboardPath } from './components/RoleProtectedRoute';
import SnackbarProvider, { useSnackbar } from './components/SnackbarProvider';
import ErrorBoundary from './components/ErrorBoundary';
import SessionMonitor from './components/SessionMonitor';
import { AppProvider } from './context/AppContext.jsx';
import { NotificationProvider } from './context/NotificationContext';
import { ChatProvider } from './context/ChatContext';
import { FontSizeProvider } from './context/FontSizeContext';
import { SettingsProvider } from './context/SettingsContext';
import { EnterpriseAuthProvider, useEnterpriseAuth } from './context/EnterpriseAuthContext';
import { SAMLProvider } from './context/SAMLContext';
import { ASCProvider } from './context/ASCContext';
import PageTitleHandler from './components/PageTitleHandler';
import { ErrorBoundaryWrapper } from './components/WithErrorBoundary';
import { patientRoutes } from './routes/patientRoutes';

// UI/UX Enhancements
import { ToastProvider } from './components/ToastNotification';
import { setupKeyboardShortcuts } from './styles/globalStyles';

// Eagerly loaded pages (login, home)
import HomePage from './pages/HomePage-modern';
import ErrorsPage from './pages/ErrorsPage.jsx';
import LoginPage from './pages/LoginPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import RequestDemoPage from './pages/RequestDemoPage';

// Safer lazy loader with retry (handles transient chunk errors / cache mismatches)
const lazyWithRetry = (factory) => {
  const start = Date.now();
  return lazy(() =>
    factory().catch((err) => {
      // If a chunk fails to load, try once after a short delay, then force a reload
      const message = String(err?.message || err);
      const chunkFailed = /Loading chunk|ChunkLoadError|Importing a module script failed/i.test(message);
      if (!chunkFailed) throw err;
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          factory().then(resolve).catch((e2) => {
            try { sessionStorage.setItem('vp.lastChunkError', `${Date.now()}`); } catch {}
            // Force a hard reload with a cache buster
            const url = new URL(window.location.href);
            url.searchParams.set('v', String(Date.now() - start));
            window.location.replace(url.toString());
            reject(e2);
          });
        }, 250);
      });
    })
  );
};

// Lazy load all other pages (using retry loader for critical routes)
const HospitalDebug = lazyWithRetry(() => import('./pages/HospitalDebug'));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage.jsx'));
const ClinicalCommandCenter = lazyWithRetry(() => import('./pages/ClinicalCommandCenter.jsx'));
const StandardPatientManagementPage = lazyWithRetry(() => import('./pages/PatientManagementPage'));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage'));
const UserProfilePage = lazyWithRetry(() => import('./pages/UserProfilePage'));
const AdminPage = lazyWithRetry(() => import('./pages/AdminPage.jsx'));
const GreenfieldSurgeriesPage = lazyWithRetry(() => import('./pages/GreenfieldSurgeriesPage.jsx'));
const StandardProtocolManagementPage = lazyWithRetry(() => import('./pages/ProtocolManagementPage.jsx'));
const QuestionnaireManagementPage = lazyWithRetry(() => import('./pages/QuestionnaireManagementPage.jsx'));
const SurgicalReadinessPage = lazyWithRetry(() => import('./pages/SurgicalReadinessPage.jsx'));
const OrderManagementPage = lazyWithRetry(() => import('./pages/OrderManagementPage.jsx'));
const ReviewQueuePage = lazyWithRetry(() => import('./pages/ReviewQueuePage.jsx'));
const AIInsightsPage = lazyWithRetry(() => import('./pages/AIInsightsPage.jsx'));
const SurgeonsPage = lazyWithRetry(() => import('./pages/SurgeonsPage.jsx'));
const AuditPage = lazyWithRetry(() => import('./pages/AuditPage.jsx'));
const MedicationHistoryPrintPage = lazyWithRetry(() =>
  import('./pages/MedicationHistoryPrintPage.jsx')
);
const MedicationHistoryBatchPrintPage = lazyWithRetry(() =>
  import('./pages/MedicationHistoryBatchPrintPage.jsx')
);

// Prefetch key routes on idle to reduce first-hit latency
function prefetchCriticalRoutes() {
  const imports = [
    () => import('./pages/DashboardPage.jsx'),
    () => import('./pages/PatientManagementPage'),
    () => import('./pages/PatientDetailPage'),
    () => import('./pages/SurgicalReadinessPage.jsx'),
    () => import('./pages/UserProfilePage'),
    () => import('./pages/AdminPage.jsx'),
    () => import('./pages/AdminAnalyticsDashboard.jsx'),
  ];
  try {
    const run = () => imports.forEach(fn => {
      try { fn(); } catch {}
    });
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => setTimeout(run, 250));
    } else {
      setTimeout(run, 1200);
    }
  } catch {}
}
// Always use optimized plugin-based PatientSummary
const PatientSummary = lazy(() => import('./features/patient-summary/PatientSummary.jsx'));
const PatientDetailPage = lazy(() => import('./pages/PatientDetailPage'));
const DocumentationManual = lazy(() => import('./components/DocumentationManual'));
const ASAProtocolCustomization = lazy(() => import('./components/ASAProtocolCustomization'));
const ProtocolVersionComparison = lazy(() => import('./components/ProtocolVersionComparison'));
const ProtocolCompliancePage = lazy(() => import('./pages/ProtocolCompliancePage'));
const SystemDiagnosticsPage = lazy(() => import('./pages/SystemDiagnosticsPage'));
const AppointmentRequestsManagement = lazy(() => import('./pages/AppointmentRequestsManagement'));

// Lazy load ASC-specific pages
const ASCDashboardPage = lazy(() => import('./pages/asc/ASCDashboardPage'));
const FacilityManagementPage = lazy(() => import('./pages/asc/FacilityManagementPage'));
const SurgerySchedulePage = lazy(() => import('./pages/asc/SurgerySchedulePage'));

// Lazy load public marketing pages
const ASCSolutionPage = lazy(() => import('./pages/ASCSolutionPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));

// ASC pages
const ASCProtocolManagementPage = lazy(() => import('./pages/asc/ProtocolManagementPage'));
const SurgeonManagementPage = lazy(() => import('./pages/asc/SurgeonManagementPage'));
const ASCPatientManagementPage = lazy(() => import('./pages/asc/PatientManagementPage'));
const ASCPreopSummaryPage = lazy(() => import('./pages/asc/PreopSummaryPage'));
const ASCSettingsPage = lazy(() => import('./pages/asc/SettingsPage'));
const PredictiveAnalyticsDashboard = lazy(() => import('./pages/PredictiveAnalyticsDashboard'));
const AdminAnalyticsDashboard = lazy(() => import('./pages/AdminAnalyticsDashboard'));
const ClinicalAnalyticsDashboard = lazy(() => import('./pages/ClinicalAnalyticsDashboard'));
const CommunicationsHub = lazy(() => import('./pages/CommunicationsHub'));
// Reuse Surgical Readiness page for ASC route as well
// (UI/UX is consistent across modes; data source is scoped by auth)
// Already imported: SurgicalReadinessPage

// Patient portal pages
const PatientDashboard = lazy(() => import('./pages/patient/PatientDashboard'));
const PatientLoginPage = lazy(() => import('./pages/patient/PatientLoginPage'));
const PatientProfilePageComponent = lazy(() => import('./pages/patient/PatientProfilePage'));
const PatientMessages = lazy(() => import('./pages/patient/PatientMessages'));
const MyAppointments = lazy(() => import('./pages/patient/MyAppointments'));
const PatientMedicalHistory = lazy(() => import('./pages/patient/PatientMedicalHistory'));
const QuestionnairesListPage = lazy(() => import('./pages/patient/QuestionnairesListPage'));
const PatientTodos = lazy(() => import('./pages/patient/PatientTodos'));

const queryClient = new QueryClient();

// Loading component for lazy loaded routes
function PageLoader() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

// AppContent component that uses EnterpriseAuth
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationInProgress = React.useRef(false);
  const lastNavigationPath = React.useRef(null);
  const navigateRef = React.useRef(navigate);
  const { user, isAuthenticated, isLoading, logout, error: authError, federationMode } = useEnterpriseAuth();
  
  // Keep navigate ref up to date
  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const { showSnackbar } = useSnackbar();

  // Setup keyboard shortcuts
  useEffect(() => {
    const cleanup = setupKeyboardShortcuts();
    return cleanup;
  }, []);

  // Prefetch common routes/modules on idle
  useEffect(() => {
    prefetchCriticalRoutes();
  }, []);

  // Determine app mode
  const appMode = React.useMemo(() => {
    // For homepage, don't determine mode yet
    if (location.pathname === '/') {
      return 'undetermined';
    }

    // Check federation mode from JWT (highest priority)
    if (federationMode === 'asc' || federationMode === 'hospital') {
      return federationMode === 'asc' ? 'asc' : 'standard';
    }

    // Check if user has a mode set
    if (user?.mode === 'asc') return 'asc';

    // Check localStorage for app mode
    const storedMode = localStorage.getItem('appMode');
    if (storedMode === 'asc') return 'asc';

    // Check URL path for mode indicator
    if (location.pathname.startsWith('/asc')) return 'asc';

    // Default to standard mode
    return 'standard';
  }, [user, federationMode, location.pathname]);

  const isHome = location.pathname === '/';

  // Feature flag: Workbench is default; legacy available via query/localStorage override
  const useWorkbench = React.useMemo(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const legacyFlag = qs.get('legacy');
      if (legacyFlag === '1' || legacyFlag === 'true') return false;
      const wbFlag = qs.get('useWorkbench');
      if (wbFlag === '0' || wbFlag === 'false') return false;
      const stored = localStorage.getItem('useWorkbench');
      if (stored === '0' || stored === 'false') return false;
      if (stored === '1' || stored === 'true') return true;
      return true;
    } catch {
      return true;
    }
  }, [location.search]);

  // Persist default-on behavior unless explicitly disabled
  React.useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const legacyFlag = qs.get('legacy');
      const wbFlag = qs.get('useWorkbench');
      const stored = localStorage.getItem('useWorkbench');
      const disableWorkbench =
        legacyFlag === '1' ||
        legacyFlag === 'true' ||
        wbFlag === '0' ||
        wbFlag === 'false' ||
        stored === '0' ||
        stored === 'false';
      if (!disableWorkbench && stored == null) {
        localStorage.setItem('useWorkbench', '1');
      }
    } catch {
      // ignore
    }
  }, [location.search]);
  const isLogin = React.useMemo(() => location.pathname.startsWith('/login'), [location.pathname]);
  const isASCLogin = React.useMemo(
    () => isAscLoginPath(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const isOAuthCallback = location.pathname === '/oauth/callback';

  // Handle logout using Enterprise Auth - memoized to prevent recreation
  const handleLogout = React.useCallback(async () => {
    await logout();
    // Unified login; preserve mode via query param
    navigate(appMode === 'asc' ? '/login?mode=asc' : '/login', { replace: true });
  }, [logout, navigate, appMode]);

  // Handle unauthorized events from API
  React.useEffect(() => {
    const handleUnauthorized = () => {
      // Ignore during initial auth loading to avoid premature redirects on hard-refresh
      if (isLoading) return;
      // Don't redirect if we're on the homepage
      if (location.pathname === '/') return;

      // Check if user actually had a session before showing expiration message
      const hadPreviousSession =
        localStorage.getItem('token') ||
        localStorage.getItem('user') ||
        localStorage.getItem('lastActivity');

      const message = hadPreviousSession
        ? 'Your session has expired. Please log in again.'
        : 'Please log in to access this page.';

      // Determine destination by context (patient, ASC, or hospital)
      const dest = location.pathname.startsWith('/patient')
        ? routes.login()
        : (appMode === 'asc' || location.pathname.startsWith('/asc'))
        ? routes.ascLogin()
        : routes.login();

      navigate(dest, { replace: true, state: { message } });
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate, appMode, location.pathname, isLoading]);

  // Legacy impersonation support (maintained for backward compatibility)
  const [impersonatedUser, setImpersonatedUser] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem('impersonation'));
    } catch {
      return null;
    }
  });

  // Helper to stop impersonation globally
  const stopImpersonation = () => {
    localStorage.removeItem('impersonation');
    setImpersonatedUser(null);
  };

  // Track impersonation state changes
  React.useEffect(() => {
    const handleStorage = () => {
      try {
        const impersonated = JSON.parse(localStorage.getItem('impersonation'));
        setImpersonatedUser(impersonated);
      } catch {
        setImpersonatedUser(null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Use impersonated user if available, otherwise use authenticated user
  const currentUser = impersonatedUser || user;

  // Route protection and navigation logic
  React.useEffect(() => {
    // ALWAYS allow access to home page without any checks
    if (location.pathname === '/') {
      return;
    }

    // Don't do anything while auth is loading - CRITICAL for preventing premature redirects
    if (isLoading) {
      return;
    }

    const currentPath = location.pathname;

    // PRIORITY 1: Handle authenticated users on login pages - redirect them to dashboard
    // This must happen BEFORE any early return conditions
    if (isAuthenticated && currentUser) {
      // Determine the correct landing page for this user
      const targetPath = currentUser.mode === 'patient' || currentUser.userType === 'patient' || currentUser.role === 'patient' || currentUser.role === 'PATIENT'
        ? '/patient/dashboard'
        : currentUser.mode === 'asc'
        ? '/asc/dashboard'  // ASC users go to ASC dashboard
        : '/patient-management';  // Hospital users go to worklist
      
      // Clear navigation tracking if we're already on the target path
      if (currentPath === targetPath && lastNavigationPath.current === targetPath) {
        // We're already where we need to be, clear the tracking
        lastNavigationPath.current = null;
        navigationInProgress.current = false;
      }
      
      if (isLogin) {
        // Check if LoginPage already handled the redirect
        const loginRedirectHandled = sessionStorage.getItem('loginRedirectHandled');
        if (loginRedirectHandled) {
          console.log('[App] LoginPage already handled redirect, skipping App.jsx navigation');
          sessionStorage.removeItem('loginRedirectHandled');
          // Clear navigation tracking since LoginPage handled it
          navigationInProgress.current = false;
          lastNavigationPath.current = null;
          return;
        }
        
        // Only navigate if LoginPage didn't handle it (fallback)
        // This should rarely happen now that LoginPage handles its own navigation
        if (!navigationInProgress.current && lastNavigationPath.current !== targetPath) {
          console.log('[App] FALLBACK: User authenticated on /login but LoginPage did not navigate');
          console.log('[App] Navigation state:', { 
            navigationInProgress: navigationInProgress.current,
            lastNavigationPath: lastNavigationPath.current,
            targetPath,
            navigateFunctionExists: typeof navigate === 'function'
          });
          
          navigationInProgress.current = true;
          lastNavigationPath.current = targetPath;
          sessionStorage.removeItem('justLoggedIn');
          
          try {
            console.log('[App] Calling navigate() to:', targetPath);
            navigateRef.current(targetPath, { replace: true });
            console.log('[App] navigate() called successfully');
          } catch (error) {
            console.error('[App] Navigation error:', error);
            navigationInProgress.current = false;
          }
          
          // Reset flag after navigation completes
          setTimeout(() => {
            console.log('[App] Resetting navigation flag');
            navigationInProgress.current = false;
          }, 500);
        }
        return;
      } else if (isASCLogin) {
        // Prevent infinite loop for ASC login
        if (!navigationInProgress.current && lastNavigationPath.current !== '/dashboard') {
          console.log('[App] User authenticated on ASC login variant, redirecting to /dashboard');
          navigationInProgress.current = true;
          lastNavigationPath.current = '/dashboard';
          sessionStorage.removeItem('justLoggedIn');
          navigateRef.current('/dashboard', { replace: true });
          setTimeout(() => {
            navigationInProgress.current = false;
          }, 500);
        }
        return;
      }
    }
    
    // PRIORITY 2: Check if we just logged in - but only for protected routes
    // This prevents redirect loops but doesn't block login->dashboard navigation
    const justLoggedIn = sessionStorage.getItem('justLoggedIn');
    if (justLoggedIn && !isLogin && !isASCLogin) {
      const loginTime = parseInt(justLoggedIn);
      const now = Date.now();
      if (now - loginTime < 10000) {
        // Just logged in and we're on a protected route, allow access
        return;
      } else {
        // Old flag, clean it up
        sessionStorage.removeItem('justLoggedIn');
      }
    }
    
    // Wait a bit after initial mount to avoid race conditions
    const hasJustMounted = !user && localStorage.getItem('token');
    if (hasJustMounted && !isLogin && !isASCLogin) {
      // We have a token but user isn't loaded yet - wait
      return;
    }

    // Define public routes that don't need auth
    const publicPaths = [
      '/', 
      '/request-demo', 
      '/login', 
      '/asc/login', // legacy alias; redirects to /login?mode=asc
      '/oauth/callback',
      '/errors',
      // '/patient/login', - deprecated, using unified /login
      '/patient/register',
      '/patient/forgot-password',
      '/patient/reset-password',
      '/patient/verify-email',
      '/asc-solution',
      '/integrations'
    ];
    const isPublicPath = publicPaths.some(
      path => currentPath === path || currentPath.startsWith('/oauth/callback')
    );

    // If it's a public path, allow access
    if (isPublicPath) {
      return;
    }

    // Define protected routes
    const standardProtectedRoutes = [
      '/patients',
      '/preop',
      '/preop-summary',
      '/settings',
      '/admin',
      '/greenfield-surgeries',
      '/documentation',
      '/dashboard',
      '/protocols',
      '/readiness',
      '/orders',
      '/ai-insights',
      '/surgeons',
      '/audit',
      '/communications',
    ];

    const ascProtectedRoutes = [
      // Note: ASC mode users currently use the regular /dashboard
      // '/asc/dashboard',  // This route doesn't exist yet
      '/asc/surgeries',
      '/asc/patients',
      '/asc/surgeons',
      '/asc/protocols',
      '/asc/facility',
      '/asc/settings',
      '/asc/admin',
      '/asc/profile',
    ];

    const patientProtectedRoutes = [
      '/patient/dashboard',
      '/patient/profile',
      '/patient/messages',
      '/patient/appointments',
      '/patient/medical-history',
      '/patient/questionnaires',
      '/patient/todos',
      '/patient/summary',
    ];

    // Check if current path is protected
    const sharedProtectedRoutes = ['/profile'];
    const isSharedRoute = sharedProtectedRoutes.some(route => currentPath.startsWith(route));
    const isStandardProtected = standardProtectedRoutes.some(route =>
      currentPath.startsWith(route)
    );
    const isASCProtected = ascProtectedRoutes.some(route => currentPath.startsWith(route));
    const isPatientProtected = patientProtectedRoutes.some(route => currentPath.startsWith(route));
    const isStandardExclusiveProtected = isStandardProtected && !isSharedRoute;
    
    if (currentPath === '/profile') {
      console.log('[App] Profile route check:', {
        currentPath,
        isSharedRoute,
        isStandardProtected,
        isStandardExclusiveProtected,
        isAuthenticated,
        currentUser,
        userMode: currentUser?.mode
      });
    }

    // Not authenticated - redirect to appropriate login
    if (!isAuthenticated) {
      // Only redirect if we're really not authenticated and auth check is complete
      const hasToken = localStorage.getItem('token');
      if (!hasToken && !navigationInProgress.current) {
        if (isPatientProtected) {
          navigationInProgress.current = true;
          navigate(routes.login(), {
            replace: true,
            state: { from: currentPath, message: 'Please log in to access your patient portal.' },
          });
          setTimeout(() => { navigationInProgress.current = false; }, 100);
        } else if (isASCProtected) {
          navigationInProgress.current = true;
          navigate(routes.ascLogin(), {
            replace: true,
            state: { from: currentPath, message: 'Please log in to access this area.' },
          });
          setTimeout(() => { navigationInProgress.current = false; }, 100);
        } else if (isStandardProtected || isSharedRoute) {
          navigationInProgress.current = true;
          navigate(routes.login(), {
            replace: true,
            state: { from: currentPath, message: 'Please log in to access this page.' },
          });
          setTimeout(() => { navigationInProgress.current = false; }, 100);
        }
      }
      return;
    }

    // Authenticated - check mode compatibility
    if (isAuthenticated && currentUser) {
      const isPatientUser = currentUser.mode === 'patient' || currentUser.userType === 'patient' || currentUser.role === 'patient' || currentUser.role === 'PATIENT';
      
      // Wrong mode access - handle cross-mode attempts
      if (isPatientProtected && !isPatientUser) {
        handleLogout().then(() => {
          navigate(routes.login(), {
            replace: true,
            state: {
              from: currentPath,
              message: 'Please log in with patient credentials to access this area.',
            },
          });
        });
      } else if (isASCProtected && currentUser.mode !== 'asc') {
        handleLogout().then(() => {
          localStorage.setItem('appMode', 'asc');
          navigate(routes.ascLogin(), {
            replace: true,
            state: {
              from: currentPath,
              message: 'Please log in with ASC credentials to access this area.',
            },
          });
        });
      } else if (isStandardExclusiveProtected && (currentUser.mode === 'asc' || isPatientUser)) {
        handleLogout().then(() => {
          localStorage.removeItem('appMode');
          navigate(routes.login(), {
            replace: true,
            state: {
              from: currentPath,
              message: 'Please log in with standard credentials to access this area.',
            },
          });
        });
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    currentUser,
    location.pathname,
    isLogin,
    isASCLogin,
    handleLogout,
    // NOTE: navigate is intentionally excluded to prevent infinite loops
  ]);

  // Show loading spinner while auth is initializing
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          fontSize: 'var(--app-font-size, 16px)',
        }}
      >
        <div>Loading...</div>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ErrorBoundary>
        {/* Session monitoring for authenticated users */}
        {isAuthenticated && currentUser && (
          <SessionMonitor 
            onSessionExpired={() => {
              handleLogout();
              showSnackbar('Your session has expired. Please log in again.', 'warning');
            }}
          />
        )}
        <Routes>
          {/* Routes without layout wrapper */}
          <Route path="/" element={<HomePage />} />
          <Route path="/request-demo" element={<RequestDemoPage />} />
          <Route path="/asc-solution" element={
            <Suspense fallback={<PageLoader />}>
              <ASCSolutionPage />
            </Suspense>
          } />
          <Route path="/integrations" element={
            <Suspense fallback={<PageLoader />}>
              <IntegrationsPage />
            </Suspense>
          } />
          {/* Simple errors page */}
          <Route path="/errors" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorsPage />
            </Suspense>
          } />
          {/* Errors viewer is routed at /errors */}
          {/* Redirect legacy login routes to unified login with mode - only if not authenticated */}
          <Route path="/hospital/login" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } />
          {/* Deprecated: /asc/login → unified /login with mode */}
          <Route path="/asc/login" element={<Navigate to="/login?mode=asc" replace />} />
          {/* Main unified login route */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
          <Route
            path="/print/patients/:patientId/med-history"
            element={
              <ProtectedRoute user={currentUser}>
                <Suspense fallback={<PageLoader />}>
                  <MedicationHistoryPrintPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/print/patients/med-history/batch"
            element={
              <ProtectedRoute user={currentUser}>
                <Suspense fallback={<PageLoader />}>
                  <MedicationHistoryBatchPrintPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Patient Portal Routes */}
          {patientRoutes}

          {/* Routes with Layout wrapper (Workbench or Legacy) */}
          <Route
            path="/*"
            element={
              (useWorkbench ? (
                <WorkbenchLayout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {console.log('[App] Rendering Workbench nested routes, current path:', location.pathname)}
                      {/* NOTE: The nested routes below remain unchanged */}
                      {/* Dashboard deprecated - redirect to patient-management */}
                      <Route
                        path="dashboard"
                        element={<Navigate to="/patient-management" replace />}
                      />
                      {/* Patient Management */}
                      <Route
                        path="patient-management"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Patient Management">
                              <StandardPatientManagementPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="review-queue"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Review Queue">
                              <ReviewQueuePage />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="questionnaires/review"
                        element={<Navigate to="/review-queue" replace />}
                      />
                      <Route
                        path="asc/review-queue"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Review Queue">
                              <ReviewQueuePage />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/questionnaires/review"
                        element={<Navigate to="/asc/review-queue" replace />}
                      />
                      <Route
                        path="protocols"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Protocol Management">
                              <StandardProtocolManagementPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="surgeons"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Surgeons">
                              <SurgeonsPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="orders"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Orders">
                              <OrderManagementPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="readiness"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Surgical Readiness">
                              <SurgicalReadinessPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="preop-summary/:patientId"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Preop Summary">
                              <PatientSummary user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="patient-summary/:patientId"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Patient Summary">
                              <PatientSummary user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      {/* Patient detail routes (Workbench parity) */}
                      <Route
                        path="patient/:patientId"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Patient Detail">
                              <Suspense fallback={<PageLoader />}>
                                <PatientDetailPage />
                              </Suspense>
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="patient/:patientId/:section"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Patient Detail">
                              <Suspense fallback={<PageLoader />}>
                                <PatientDetailPage />
                              </Suspense>
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="settings"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Settings">
                              <SettingsPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="profile"
                        element={
                          <>
                            {console.log('[App] Profile route matched! Rendering UserProfilePage')}
                            <ProtectedRoute user={currentUser}>
                              <ErrorBoundaryWrapper type="page" name="User Profile">
                                <UserProfilePage user={currentUser} />
                              </ErrorBoundaryWrapper>
                            </ProtectedRoute>
                          </>
                        }
                      />
                      <Route
                        path="profile/*"
                        element={
                          <ProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="User Profile">
                              <UserProfilePage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="admin"
                        element={
                          <ProtectedRoute
                            user={currentUser && currentUser.role === 'ADMIN' ? currentUser : null}
                          >
                            <ErrorBoundaryWrapper type="page" name="Admin Panel">
                              <AdminPage
                                user={currentUser}
                                setUser={user => {
                                  if (user !== currentUser) {
                                    localStorage.setItem('impersonation', JSON.stringify(user));
                                    setImpersonatedUser(user);
                                  }
                                }}
                                stopImpersonation={stopImpersonation}
                              />
                            </ErrorBoundaryWrapper>
                          </ProtectedRoute>
                        }
                      />
                      {/* ASC Routes - must be above catch-all */}
                      <Route
                        path="asc/dashboard"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Dashboard">
                              <ASCDashboardPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/readiness"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Surgical Readiness">
                              <SurgicalReadinessPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/review-queue"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Review Queue">
                              <ReviewQueuePage />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/facility-management"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Facility Management">
                              <FacilityManagementPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/surgery-schedule"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Surgery Schedule">
                              <SurgerySchedulePage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/protocol-management"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Protocol Management">
                              <ASCProtocolManagementPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/surgeon-management"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="Surgeon Management">
                              <SurgeonManagementPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/orders"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Orders">
                              <OrderManagementPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/patient-management"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Patient Management">
                              <ASCPatientManagementPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/preop-summary/:patientId"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Preop Summary">
                              <ASCPreopSummaryPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/settings"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Settings">
                              <ASCSettingsPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/admin"
                        element={
                          <ASCProtectedRoute
                            user={currentUser && currentUser.role === 'ADMIN' ? currentUser : null}
                          >
                            <ErrorBoundaryWrapper type="page" name="ASC Admin">
                              <AdminPage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      <Route
                        path="asc/profile"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <ErrorBoundaryWrapper type="page" name="ASC Profile">
                              <UserProfilePage user={currentUser} />
                            </ErrorBoundaryWrapper>
                          </ASCProtectedRoute>
                        }
                      />
                      {/* Fallback for other ASC routes */}
                      <Route
                        path="asc/*"
                        element={
                          <ASCProtectedRoute user={currentUser}>
                            <Navigate to="/asc/dashboard" replace />
                          </ASCProtectedRoute>
                        }
                      />
                      {/* Keep remaining routes identical to legacy wrapper */}
                      {/* Catch-all redirects to patient-management */}
                      <Route path="*" element={<Navigate to="/patient-management" replace />} />
                    </Routes>
                  </Suspense>
                </WorkbenchLayout>
              ) : (
              <VirtualPATLayout
                appMode={appMode}
                user={currentUser}
                handleLogout={handleLogout}
                stopImpersonation={stopImpersonation}
              >
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Dashboard deprecated - redirect to patient-management */}
                    <Route
                      path="dashboard"
                      element={<Navigate to="/patient-management" replace />}
                    />
                    {/* ... existing route list continues unchanged ... */}
                    <Route
                      path="clinical-command-center"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Clinical Command Center">
                            <ClinicalCommandCenter user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    {/* Role-based analytics routes */}
                    <Route
                      path="admin/analytics"
                      element={
                        <RoleProtectedRoute allowedRoles={ROLE_GROUPS.ADMIN}>
                          <ErrorBoundaryWrapper type="page" name="Admin Analytics">
                            <AdminAnalyticsDashboard />
                          </ErrorBoundaryWrapper>
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="clinical/analytics"
                      element={
                        <RoleProtectedRoute allowedRoles={[...ROLE_GROUPS.CLINICAL, ...ROLE_GROUPS.ADMIN]}>
                          <ErrorBoundaryWrapper type="page" name="Clinical Analytics">
                            <ClinicalAnalyticsDashboard />
                          </ErrorBoundaryWrapper>
                        </RoleProtectedRoute>
                      }
                    />
                    {/* Legacy route - redirects based on role */}
                    <Route
                      path="predictive-analytics"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <Navigate to={getUserDashboardPath(currentUser)} replace />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="communications"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Communications Hub">
                            <CommunicationsHub />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="patient-management"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Management">
                            <StandardPatientManagementPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="review-queue"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Review Queue">
                            <ReviewQueuePage />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="questionnaires/review"
                      element={<Navigate to="/review-queue" replace />}
                    />
                    <Route
                      path="patients"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patients">
                            <StandardPatientManagementPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="patients/:patientId"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Details">
                            <StandardPatientManagementPage user={currentUser} initialTab={1} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    {/* New patient detail routes with proper URL structure */}
                    <Route
                      path="patient/:patientId"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Detail">
                            <Suspense fallback={<PageLoader />}>
                              <PatientDetailPage />
                            </Suspense>
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="patient/:patientId/:section"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Detail">
                            <Suspense fallback={<PageLoader />}>
                              <PatientDetailPage />
                            </Suspense>
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="appointment-requests"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Appointment Requests">
                            <AppointmentRequestsManagement user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="settings"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Settings">
                            <SettingsPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="profile"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="User Profile">
                            <UserProfilePage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="profile/*"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="User Profile">
                            <UserProfilePage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin"
                      element={
                        <ProtectedRoute
                          user={currentUser && currentUser.role === 'ADMIN' ? currentUser : null}
                        >
                          <ErrorBoundaryWrapper type="page" name="Admin Panel">
                            <AdminPage
                              user={currentUser}
                              setUser={user => {
                                // Handle impersonation through localStorage for backward compatibility
                                if (user !== currentUser) {
                                  localStorage.setItem('impersonation', JSON.stringify(user));
                                  setImpersonatedUser(user);
                                }
                              }}
                              stopImpersonation={stopImpersonation}
                            />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="greenfield-surgeries"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Greenfield Surgeries">
                            <GreenfieldSurgeriesPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="protocols"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Protocol Management">
                            <StandardProtocolManagementPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="questionnaires"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Questionnaire Management">
                            <QuestionnaireManagementPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="protocols/asa/:protocolId/customize"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="ASA Protocol Customization">
                            <ASAProtocolCustomization user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="protocols/asa/:protocolId/history"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Protocol Version History">
                            <ProtocolVersionComparison user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="rules"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Rules Management">
                            <AdminPage user={currentUser} initialTab={1} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="surgeries"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Surgeries Management">
                            <GreenfieldSurgeriesPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="documentation"
                      element={
                        <ErrorBoundaryWrapper type="page" name="Documentation">
                          <DocumentationManual />
                        </ErrorBoundaryWrapper>
                      }
                    />
                    <Route
                      path="preop-summary/:patientId"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Preop Summary">
                            <PatientSummary user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    {/* New PatientSummary route for direct access during testing */}
                    <Route
                      path="patient-summary/:patientId"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Summary">
                            <PatientSummary user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="protocol-compliance"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Protocol Compliance">
                            <ProtocolCompliancePage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="protocol-compliance/:patientId"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Protocol Compliance">
                            <ProtocolCompliancePage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />

                    {/* Debug route for hospital mode */}
                    <Route
                      path="debug/hospital"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Hospital Debug">
                            <HospitalDebug />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />

                    {/* System diagnostics route */}
                    <Route
                      path="system-diagnostics"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="System Diagnostics">
                            <SystemDiagnosticsPage />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />

                    {/* New Feature Routes */}
                    <Route
                      path="readiness"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Surgical Readiness">
                            <SurgicalReadinessPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="orders"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Order Management">
                            <OrderManagementPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    {/* Deprecated: /ai-insights now unified under /clinical/analytics */}
                    <Route path="ai-insights" element={<Navigate to="/clinical/analytics" replace />} />
                    <Route
                      path="surgeons"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Surgeons">
                            <SurgeonsPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="audit"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Audit Log">
                            <AuditPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* ASC Routes */}
                    <Route
                      path="asc/readiness"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="ASC Surgical Readiness">
                            <SurgicalReadinessPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/review-queue"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="ASC Review Queue">
                            <ReviewQueuePage />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/questionnaires/review"
                      element={<Navigate to="/asc/review-queue" replace />}
                    />
                    <Route
                      path="asc/dashboard"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="ASC Dashboard">
                            <ASCDashboardPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/facility-management"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Facility Management">
                            <FacilityManagementPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/surgery-schedule"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Surgery Schedule">
                            <SurgerySchedulePage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/protocol-management"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="ASC Protocol Management">
                            <ASCProtocolManagementPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/surgeon-management"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Surgeon Management">
                            <SurgeonManagementPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/patient-management"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="ASC Patient Management">
                            <ASCPatientManagementPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/preop-summary/:patientId"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="ASC Preop Summary">
                            <ASCPreopSummaryPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/settings"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="ASC Settings">
                            <ASCSettingsPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/admin"
                      element={
                        <ASCProtectedRoute
                          user={currentUser && currentUser.role === 'ADMIN' ? currentUser : null}
                        >
                          <ErrorBoundaryWrapper type="page" name="ASC Admin">
                            <AdminPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    <Route
                      path="asc/profile"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="ASC Profile">
                            <UserProfilePage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ASCProtectedRoute>
                      }
                    />
                    {/* Fallback for any other /asc route to ASC dashboard if authenticated */}
                    <Route
                      path="asc/*"
                      element={
                        <ASCProtectedRoute user={currentUser}>
                          <Navigate to="/dashboard" replace />
                        </ASCProtectedRoute>
                      }
                    />

                    {/* Patient Portal Routes */}
                    <Route
                      path="patient/login"
                      element={
                        <ErrorBoundaryWrapper type="page" name="Patient Login">
                          <PatientLoginPage />
                        </ErrorBoundaryWrapper>
                      }
                    />
                    <Route
                      path="patient/dashboard"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Dashboard">
                            <PatientDashboard user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="patient/profile"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Profile">
                            <PatientProfilePageComponent user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="patient/messages"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Messages">
                            <PatientMessages user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="patient/appointments"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="My Appointments">
                            <MyAppointments user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="patient/medical-history"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Medical History">
                            <PatientMedicalHistory user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="patient/questionnaires"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Questionnaires">
                            <QuestionnairesListPage user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="patient/todos"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <ErrorBoundaryWrapper type="page" name="Patient Todos">
                            <PatientTodos user={currentUser} />
                          </ErrorBoundaryWrapper>
                        </ProtectedRoute>
                      }
                    />
                    {/* Fallback for any other /patient route to patient dashboard if authenticated */}
                    <Route
                      path="patient/*"
                      element={
                        <ProtectedRoute user={currentUser}>
                          <Navigate to="/patient/dashboard" replace />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </VirtualPATLayout>
              ))
            }
          />
        </Routes>
      </ErrorBoundary>
    </Box>
  );
}

function RealtimeProviders({ children }) {
  const location = useLocation();
  const disableRealtime = location.pathname.startsWith('/print/');

  return (
    <NotificationProvider disabled={disableRealtime}>
      <ChatProvider disabled={disableRealtime}>{children}</ChatProvider>
    </NotificationProvider>
  );
}

// Main App component that provides EnterpriseAuth
function App() {
  return (
    <SettingsProvider>
      <FontSizeProvider>
        <PageTitleHandler />
        <AppProvider>
          <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <SnackbarProvider>
                <ToastProvider>
                  <EnterpriseAuthProvider>
                    <RealtimeProviders>
                      <SAMLProvider>
                        <ASCProvider>
                          <AppContent />
                        </ASCProvider>
                      </SAMLProvider>
                    </RealtimeProviders>
                  </EnterpriseAuthProvider>
                </ToastProvider>
              </SnackbarProvider>
            </LocalizationProvider>
          </QueryClientProvider>
        </AppProvider>
      </FontSizeProvider>
    </SettingsProvider>
  );
}

export default App;
